import { Language } from './i18n';

/**
 * Route definitions with localized paths for each language.
 * 
 * Following the "Native Root, Explicit English" SEO strategy:
 * - Italian homepage is at root `/` (no `/it/` prefix for domain authority)
 * - English pages use `/en/` prefix
 * - Italian subpages use `/it/` prefix
 * 
 * @example
 * // Direct access
 * routes.home.en        // '/en'
 * routes.home.it        // '/'
 * routes.requestInfo.en // '/en/request-info'
 * 
 * // With language variable
 * routes.home[language]
 * routes.contactReceived[language]
 */
export const routes = {
    // Homepage - Native Root strategy
    home: {
        en: '/en',
        it: '/',
    },

    // Request Info
    requestInfo: {
        en: '/en/request-info',
        it: '/it/richiedi-informazioni',
    },

    // Contact Received (success page)
    contactReceived: {
        en: '/en/contact-received',
        it: '/it/contatto-ricevuto',
    },

    // Auth
    auth: {
        en: '/auth',
        it: '/auth',
    },

    // Docs (same URL, content changes based on language)
    privacyPolicy: {
        en: '/docs/privacy-policy',
        it: '/docs/privacy-policy',
    },
    termsOfService: {
        en: '/docs/terms-of-service',
        it: '/docs/terms-of-service',
    },
} as const;

/**
 * Find the equivalent route in another language given the current path.
 * Used by the language switcher to navigate to the same page in a different language.
 * 
 * @param currentPath - The current URL pathname
 * @param targetLanguage - The language to switch to
 * @returns The equivalent path in the target language
 */
export function getLocalizedPath(currentPath: string, targetLanguage: Language): string {
    // Normalize the path (remove trailing slash except for root)
    const normalizedPath = currentPath === '/' ? '/' : currentPath.replace(/\/$/, '');

    // Check each route to find a match
    for (const route of Object.values(routes)) {
        // Check if current path matches either language version
        if (route.en === normalizedPath || route.it === normalizedPath) {
            return route[targetLanguage];
        }
    }

    // For paths not in our route map, apply smart language prefix swapping
    const pathSegments = normalizedPath.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];

    // Handle root path
    if (normalizedPath === '/' || pathSegments.length === 0) {
        return targetLanguage === 'en' ? '/en' : '/';
    }

    // If the first segment is a language code, swap it
    if (firstSegment === 'en' || firstSegment === 'it') {
        if (targetLanguage === 'it' && pathSegments.length === 1 && firstSegment === 'en') {
            // /en -> / (homepage)
            return '/';
        }
        pathSegments[0] = targetLanguage;
        return '/' + pathSegments.join('/');
    }

    // Path doesn't have a language prefix
    if (targetLanguage === 'en') {
        // Add /en prefix
        return '/en' + normalizedPath;
    }

    // For Italian, keep as is (native root strategy)
    return normalizedPath;
}

/**
 * Determine the current language from a URL path.
 * 
 * @param pathname - The URL pathname
 * @returns The detected language
 */
export function getLanguageFromPath(pathname: string): Language {
    const firstSegment = pathname.split('/').filter(Boolean)[0];

    if (firstSegment === 'en') {
        return 'en';
    }

    // Root and /it/ paths are Italian
    return 'it';
}
